import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit, Save, X, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import { useFormWithFields } from "@/hooks/useForms";
import { FormFieldOption } from "@/components/forms/HierarchicalOptionsEditor";
import { slugify } from "@/lib/utils";

interface QuickFormFieldsEditorProps {
  formId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFormCreated?: (formId: string) => void;
}

export function QuickFormFieldsEditor({
  formId,
  open,
  onOpenChange,
  onFormCreated,
}: QuickFormFieldsEditorProps) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>("");
  const [newFormNome, setNewFormNome] = useState("");
  const [formCreated, setFormCreated] = useState(false);
  const isCreatingNewForm = !formId && !formCreated;
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [editingProductPrice, setEditingProductPrice] = useState<number | null>(null);
  const [newOptionInputs, setNewOptionInputs] = useState<Record<string, string>>({});
  const [deletingOptionId, setDeletingOptionId] = useState<string | null>(null);
  const [deletingRelation, setDeletingRelation] = useState<{
    optionId: string;
    relatedOptionId: string;
  } | null>(null);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editingFieldNome, setEditingFieldNome] = useState("");
  const [editingFieldTipo, setEditingFieldTipo] = useState<string>("");
  const [editingFieldDescricao, setEditingFieldDescricao] = useState("");
  const [editingFieldModificaValor, setEditingFieldModificaValor] = useState(false);
  const [editingFieldObrigatorio, setEditingFieldObrigatorio] = useState(false);
  const [editingFieldFiltradoPor, setEditingFieldFiltradoPor] = useState<string | null>(null);
  const [isAddingNewField, setIsAddingNewField] = useState(false);

  // Estado para armazenar o ID do formulário sendo criado/editado
  const [currentFormId, setCurrentFormId] = useState<string | null>(formId);

  // Atualizar currentFormId quando formId mudar (mas não resetar se já temos um currentFormId válido de um formulário recém-criado)
  useEffect(() => {
    if (formId && formId !== currentFormId) {
      setCurrentFormId(formId);
      // Só resetar formCreated se não estamos no meio de criar um formulário
      // Se formCreated já é true, significa que acabamos de criar um formulário
      // e não queremos resetar isso até que o usuário feche o modal
      if (!formCreated) {
        setFormCreated(false);
      }
    }
  }, [formId, currentFormId, formCreated]);

  // Resetar estado quando o modal fechar
  useEffect(() => {
    if (!open) {
      // Não resetar currentFormId se formId prop ainda está definido
      // Isso permite que o modal mantenha o estado quando reabrir
      if (!formId) {
        setCurrentFormId(null);
        setFormCreated(false);
        setNewFormNome("");
      }
    }
  }, [open, formId]);

  const { data: formData, isLoading } = useFormWithFields(currentFormId || "", {
    enabled: !!currentFormId && open,
  });

  // Filtrar apenas campos do tipo select ou multiselect
  const selectFields = formData?.fields?.filter(
    (f: any) => f.tipo === "select" || f.tipo === "multiselect"
  ) || [];

  // Buscar relacionamentos de opções
  const { data: optionRelations = [] } = useQuery({
    queryKey: ["form-option-relations", currentFormId],
    enabled: !!currentFormId && open && !!formData?.fields,
    queryFn: async () => {
      if (!formData?.fields) return [];
      
      const allOptionIds = formData.fields
        .flatMap((f: any) => {
          const flatten = (opts: any[]): string[] => {
            const ids: string[] = [];
            for (const opt of opts || []) {
              ids.push(opt.id);
              if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
                ids.push(...flatten(opt.sub_opcoes));
              }
            }
            return ids;
          };
          return flatten(f.options || []);
        });

      if (allOptionIds.length === 0) return [];

      const { data, error } = await supabase
        .from("form_field_option_relations")
        .select("*")
        .in("option_id", allOptionIds);

      if (error && error.code !== "PGRST116") {
        console.error("Erro ao buscar relacionamentos:", error);
        return [];
      }

      return data || [];
    },
  });

  // Mapear dependências por campo
  const dependenciesByField = useMemo(() => {
    const map = new Map<string, any>();
    (formData?.dependencies || []).forEach((dep: any) => {
      map.set(dep.field_id, dep);
    });
    return map;
  }, [formData?.dependencies]);

  // Mapear relacionamentos por opção
  const relationsByOption = useMemo(() => {
    const map = new Map<string, string[]>();
    optionRelations.forEach((rel: any) => {
      if (!map.has(rel.option_id)) {
        map.set(rel.option_id, []);
      }
      map.get(rel.option_id)!.push(rel.related_option_id);
    });
    return map;
  }, [optionRelations]);

  // Obter campo pai para um campo filho
  const getParentField = (fieldId: string) => {
    const dependency = dependenciesByField.get(fieldId);
    if (!dependency) return null;
    return formData?.fields?.find((f: any) => f.id === dependency.depends_on_field_id);
  };

  // Obter campos filhos (que dependem deste campo)
  const getChildFields = (fieldId: string) => {
    return formData?.fields?.filter((f: any) => {
      const dependency = dependenciesByField.get(f.id);
      return dependency?.depends_on_field_id === fieldId;
    }) || [];
  };

  // Obter opções relacionadas para uma opção (do ponto de vista do campo filho)
  const getRelatedOptions = (optionId: string) => {
    const relatedIds = relationsByOption.get(optionId) || [];
    if (relatedIds.length === 0) return [];
    
    // Buscar em todos os campos
    const allOptions: any[] = [];
    formData?.fields?.forEach((f: any) => {
      const flatten = (opts: any[]): any[] => {
        const result: any[] = [];
        for (const opt of opts || []) {
          result.push(opt);
          if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
            result.push(...flatten(opt.sub_opcoes));
          }
        }
        return result;
      };
      allOptions.push(...flatten(f.options || []));
    });
    
    return relatedIds
      .map(id => allOptions.find(opt => opt.id === id))
      .filter(Boolean);
  };

  // Obter opções relacionadas de forma inversa (do ponto de vista do campo pai)
  // Retorna quais opções dos campos filhos estão relacionadas com esta opção do campo pai
  const getChildOptionsRelatedToParent = (parentOptionId: string) => {
    // Buscar relacionamentos onde related_option_id = parentOptionId
    const relations = optionRelations.filter((rel: any) => rel.related_option_id === parentOptionId);
    const childOptionIds = relations.map((rel: any) => rel.option_id);
    
    if (childOptionIds.length === 0) return [];
    
    // Buscar em todos os campos
    const allOptions: any[] = [];
    formData?.fields?.forEach((f: any) => {
      const flatten = (opts: any[]): any[] => {
        const result: any[] = [];
        for (const opt of opts || []) {
          result.push(opt);
          if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
            result.push(...flatten(opt.sub_opcoes));
          }
        }
        return result;
      };
      allOptions.push(...flatten(f.options || []));
    });
    
    return childOptionIds
      .map(id => allOptions.find(opt => opt.id === id))
      .filter(Boolean);
  };

  // Definir primeira aba como ativa quando os dados carregarem
  useEffect(() => {
    if (!activeTab) {
      setActiveTab("all-fields");
    }
  }, [activeTab]);

  const updateOptionMutation = useMutation({
    mutationFn: async ({
      optionId,
      label,
      valorProduto,
      fieldModificaValor,
    }: {
      optionId: string;
      label: string;
      valorProduto: number | null;
      fieldModificaValor?: boolean;
    }) => {
      const updateData: any = {
        label,
        valor: slugify(label),
      };
      
      // Só atualizar valor_produto se o campo modifica valor do produto
      if (fieldModificaValor) {
        updateData.valor_produto = valorProduto;
      }
      
      const { error } = await supabase
        .from("form_field_options")
        .update(updateData)
        .eq("id", optionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
      toast.success("Opção atualizada");
      setEditingOptionId(null);
      setEditingLabel("");
      setEditingProductPrice(null);
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar opção: " + error.message);
    },
  });

  const createOptionMutation = useMutation({
    mutationFn: async ({
      fieldId,
      label,
      valorProduto,
      ordem,
    }: {
      fieldId: string;
      label: string;
      valorProduto: number | null;
      ordem: number;
    }) => {
      const { data: newOption, error } = await supabase
        .from("form_field_options")
        .insert({
          field_id: fieldId,
          label,
          valor: slugify(label),
          valor_produto: valorProduto,
          ordem,
          tenant_id: tenantId!,
        })
        .select()
        .single();

      if (error) throw error;
      return newOption;
    },
    onSuccess: (newOption) => {
      // Colocar a nova opção em modo de edição automaticamente ANTES de invalidar a query
      if (newOption) {
        setEditingOptionId(newOption.id);
        setEditingLabel(newOption.label || "");
        setEditingProductPrice(newOption.valor_produto);
      }
      // Invalidar a query para recarregar os dados
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
      toast.success("Opção criada");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar opção: " + error.message);
    },
  });

  const deleteOptionMutation = useMutation({
    mutationFn: async (optionId: string) => {
      const { error } = await supabase
        .from("form_field_options")
        .delete()
        .eq("id", optionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
      toast.success("Opção excluída");
    },
    onError: (error: Error) => {
      toast.error("Erro ao excluir opção: " + error.message);
    },
  });

  const flattenOptions = (options: FormFieldOption[]): FormFieldOption[] => {
    const result: FormFieldOption[] = [];
    for (const opt of options) {
      result.push(opt);
      if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
        result.push(...flattenOptions(opt.sub_opcoes));
      }
    }
    return result;
  };


  const handleSaveEdit = (fieldModificaValor?: boolean) => {
    if (!editingOptionId || !editingLabel.trim()) return;
    updateOptionMutation.mutate({
      optionId: editingOptionId,
      label: editingLabel.trim(),
      valorProduto: editingProductPrice,
      fieldModificaValor,
    });
  };

  const handleCancelEdit = () => {
    setEditingOptionId(null);
    setEditingLabel("");
    setEditingProductPrice(null);
  };

  const handleEditOption = (option: FormFieldOption) => {
    if (editingOptionId === option.id) {
      // Se já está editando, cancelar
      handleCancelEdit();
    } else {
      // Iniciar edição
      setEditingOptionId(option.id);
      setEditingLabel(option.label);
      setEditingProductPrice(option.valor_produto ?? null);
    }
  };

  const handleAddOption = (fieldId: string) => {
    const field = selectFields.find((f: any) => f.id === fieldId);
    if (!field) return;

    const allOptions = flattenOptions(field.options || []);
    const newOrdem = allOptions.length;

    createOptionMutation.mutate({
      fieldId,
      label: "Nova Opção",
      valorProduto: null,
      ordem: newOrdem,
    });
  };

  const handleCreateAndRelateOption = async (
    fieldId: string,
    label: string,
    relatedOptionId: string,
    isParentField: boolean
  ) => {
    if (!label.trim()) return;

    const allOptions = flattenOptions(
      (formData?.fields?.find((f: any) => f.id === fieldId)?.options || [])
    );
    const newOrdem = allOptions.length;

    // Criar opção
    const { data: newOption, error: optionError } = await supabase
      .from("form_field_options")
      .insert({
        field_id: fieldId,
        label: label.trim(),
        valor: slugify(label.trim()),
        ordem: newOrdem,
        tenant_id: tenantId!,
      })
      .select()
      .single();

    if (optionError) {
      toast.error("Erro ao criar opção: " + optionError.message);
      return;
    }

    // Criar relacionamento
    if (isParentField) {
      // Nova opção do campo filho relacionada com opção do campo pai
      // Verificar se já existe
      const { data: existing, error: checkError } = await supabase
        .from("form_field_option_relations")
        .select("id")
        .eq("option_id", newOption.id)
        .eq("related_option_id", relatedOptionId)
        .single();

      if (checkError && checkError.code !== "PGRST116") {
        toast.error("Erro ao verificar relacionamento: " + checkError.message);
        return;
      }

      if (!existing) {
        const { error: relationError } = await supabase
          .from("form_field_option_relations")
          .insert({
            option_id: newOption.id,
            related_option_id: relatedOptionId,
            tenant_id: tenantId!,
          });
        if (relationError) {
          toast.error("Erro ao criar relacionamento: " + relationError.message);
          return;
        }
        queryClient.setQueryData<any[]>(["form-option-relations", currentFormId], (prev) => {
          const list = Array.isArray(prev) ? prev.slice() : [];
          list.push({ option_id: newOption.id, related_option_id: relatedOptionId });
          return list;
        });
      }
    } else {
      // Nova opção do campo pai relacionada com opção do campo filho
      // Verificar se já existe
      const { data: existing, error: checkError } = await supabase
        .from("form_field_option_relations")
        .select("id")
        .eq("option_id", relatedOptionId)
        .eq("related_option_id", newOption.id)
        .single();

      if (checkError && checkError.code !== "PGRST116") {
        toast.error("Erro ao verificar relacionamento: " + checkError.message);
        return;
      }

      if (!existing) {
        const { error: relationError } = await supabase
          .from("form_field_option_relations")
          .insert({
            option_id: relatedOptionId,
            related_option_id: newOption.id,
            tenant_id: tenantId!,
          });
        if (relationError) {
          toast.error("Erro ao criar relacionamento: " + relationError.message);
          return;
        }
        queryClient.setQueryData<any[]>(["form-option-relations", currentFormId], (prev) => {
          const list = Array.isArray(prev) ? prev.slice() : [];
          list.push({ option_id: relatedOptionId, related_option_id: newOption.id });
          return list;
        });
      }
    }

    queryClient.invalidateQueries({ queryKey: ["form-option-relations", currentFormId] });
    queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
    toast.success("Opção e relacionamento criados");
    
    // Limpar input
    setNewOptionInputs((prev) => {
      const key = `${fieldId}-${relatedOptionId}`;
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  };

  // Mutations para relacionamentos
  const createRelationMutation = useMutation({
    mutationFn: async ({
      optionId,
      relatedOptionId,
    }: {
      optionId: string;
      relatedOptionId: string;
    }) => {
      // Verificar se o relacionamento já existe
      const { data: existing, error: checkError } = await supabase
        .from("form_field_option_relations")
        .select("id")
        .eq("option_id", optionId)
        .eq("related_option_id", relatedOptionId)
        .single();

      if (checkError && checkError.code !== "PGRST116") {
        // PGRST116 = no rows returned, que é o que queremos
        throw checkError;
      }

      if (existing) {
        // Relacionamento já existe, não precisa criar
        return;
      }

      const { error } = await supabase.from("form_field_option_relations").insert({
        option_id: optionId,
        related_option_id: relatedOptionId,
        tenant_id: tenantId!,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
      queryClient.invalidateQueries({ queryKey: ["form-option-relations", currentFormId] });
      toast.success("Relacionamento criado");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar relacionamento: " + error.message);
    },
  });

  const deleteRelationMutation = useMutation({
    mutationFn: async ({
      optionId,
      relatedOptionId,
    }: {
      optionId: string;
      relatedOptionId: string;
    }) => {
      const { error } = await supabase
        .from("form_field_option_relations")
        .delete()
        .eq("option_id", optionId)
        .eq("related_option_id", relatedOptionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-option-relations", currentFormId] });
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
      toast.success("Relacionamento removido");
    },
    onError: (error: Error) => {
      toast.error("Erro ao remover relacionamento: " + error.message);
    },
  });

  // Todos os campos do formulário
  const allFields = formData?.fields || [];

  // Verificar se algum outro campo já tem modifica_valor_produto ativo
  const hasOtherFieldWithProductPrice = useMemo(() => {
    if (!editingFieldId) {
      // Se não está editando nenhum campo, verificar se algum campo tem ativo
      return allFields.some((f: any) => f.modifica_valor_produto);
    }
    // Se está editando, verificar se algum outro campo (diferente do atual) tem ativo
    return allFields.some(
      (f: any) => f.id !== editingFieldId && f.modifica_valor_produto
    );
  }, [allFields, editingFieldId]);

  // Verificar se o switch deve estar desabilitado
  const isModificaValorProdutoDisabled = useMemo(() => {
    // Se já tem outro campo com modifica_valor_produto ativo
    // E o campo atual não está ativo, então desabilitar
    if (hasOtherFieldWithProductPrice && !editingFieldModificaValor) {
      return true;
    }
    return false;
  }, [hasOtherFieldWithProductPrice, editingFieldModificaValor]);

  // Sensors para drag and drop - DEVE estar antes de qualquer return condicional
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Mutation para atualizar campo - DEVE estar antes de qualquer return condicional
  const updateFieldMutation = useMutation({
    mutationFn: async ({
      fieldId,
      nome,
      tipo,
      descricao,
      modifica_valor_produto,
      obrigatorio,
    }: {
      fieldId: string;
      nome: string;
      tipo: string;
      descricao?: string;
      modifica_valor_produto: boolean;
      obrigatorio: boolean;
    }) => {
      const { error } = await supabase
        .from("form_fields")
        .update({
          nome,
          tipo,
          descricao: descricao || null,
          modifica_valor_produto,
          obrigatorio,
        })
        .eq("id", fieldId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
      toast.success("Campo atualizado");
      setEditingFieldId(null);
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar campo: " + error.message);
    },
  });

  // Mutation para criar/atualizar dependência - DEVE estar antes de qualquer return condicional
  const createOrUpdateDependencyMutation = useMutation({
    mutationFn: async ({
      fieldId,
      dependsOnFieldId,
    }: {
      fieldId: string;
      dependsOnFieldId: string;
    }) => {
      // Verificar se já existe dependência para este campo
      const { data: existing } = await supabase
        .from("form_field_dependencies")
        .select("*")
        .eq("field_id", fieldId)
        .maybeSingle();

      if (existing) {
        // Atualizar dependência existente
        const { error } = await supabase
          .from("form_field_dependencies")
          .update({ depends_on_field_id: dependsOnFieldId })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        // Criar nova dependência
        const { error } = await supabase
          .from("form_field_dependencies")
          .insert({
            field_id: fieldId,
            depends_on_field_id: dependsOnFieldId,
            condition_value: null,
            condition_operator: "equals",
            tenant_id: tenantId!,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
      toast.success("Dependência atualizada");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar dependência: " + error.message);
    },
  });

  // Mutation para remover dependência - DEVE estar antes de qualquer return condicional
  const removeDependencyMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      const { error } = await supabase
        .from("form_field_dependencies")
        .delete()
        .eq("field_id", fieldId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
      toast.success("Dependência removida");
    },
    onError: (error: Error) => {
      toast.error("Erro ao remover dependência: " + error.message);
    },
  });

  // Mutation para criar formulário - DEVE estar antes de qualquer return condicional
  const createFormMutation = useMutation({
    mutationFn: async (formNome: string) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");
      
      const slug = slugify(formNome);
      
      const { data: formData, error } = await supabase
        .from("forms")
        .insert({
          nome: formNome,
          slug: slug,
          status: "ativo",
          tenant_id: tenantId,
        })
        .select()
        .single();

      if (error) throw error;
      return formData;
    },
    onSuccess: (formData) => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      toast.success("Formulário criado com sucesso");
      // Atualizar estados antes de chamar onFormCreated para evitar race conditions
      setCurrentFormId(formData.id);
      setFormCreated(true);
      setNewFormNome("");
      // Chamar onFormCreated por último para que o componente pai atualize o formId prop
      // Isso fará com que o useEffect atualize o currentFormId, mas como formCreated=true,
      // a condição de criação não será mais verdadeira
      onFormCreated?.(formData.id);
      // Não fechar o modal, apenas atualizar o formId para mostrar a interface de edição
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar formulário: " + error.message);
    },
  });


  // Mutation para criar campo - DEVE estar antes de qualquer return condicional
  const createFieldMutation = useMutation({
    mutationFn: async ({
      nome,
      tipo,
      descricao,
      modifica_valor_produto,
      obrigatorio,
      ordem,
      currentFormId,
    }: {
      nome: string;
      tipo: string;
      descricao?: string;
      modifica_valor_produto: boolean;
      obrigatorio: boolean;
      ordem: number;
      currentFormId: string;
    }) => {
      const slug = slugify(nome);
      const { error } = await supabase.from("form_fields").insert({
        form_id: currentFormId,
        nome,
        slug,
        tipo,
        descricao: descricao || null,
        modifica_valor_produto,
        obrigatorio,
        ordem,
        tenant_id: tenantId!,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
      toast.success("Campo criado");
      setIsAddingNewField(false);
      setEditingFieldNome("");
      setEditingFieldTipo("");
      setEditingFieldDescricao("");
      setEditingFieldModificaValor(false);
      setEditingFieldObrigatorio(false);
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar campo: " + error.message);
    },
  });

  // Mutation para atualizar ordem dos campos - DEVE estar antes de qualquer return condicional
  const updateFieldOrderMutation = useMutation({
    mutationFn: async (fields: Array<{ id: string; ordem: number }>) => {
      const updates = fields.map((field) =>
        supabase
          .from("form_fields")
          .update({ ordem: field.ordem })
          .eq("id", field.id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) {
        throw errors[0].error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", currentFormId] });
    },
    onError: (error: Error) => {
      toast.error("Erro ao reordenar campos: " + error.message);
    },
  });

  // Helper para obter campos disponíveis como pais (para "Filtrado pelo Campo")
  const availableParentFields = useMemo(() => {
    if (!editingFieldId || !formData?.fields) return [];
    return formData.fields.filter(
      (field: any) =>
        field.id !== editingFieldId &&
        ["select", "multiselect", "radio"].includes(field.tipo)
    );
  }, [formData?.fields, editingFieldId]);

  // Handlers - podem estar antes dos returns
  const handleEditField = (field: any) => {
    setEditingFieldId(field.id);
    setEditingFieldNome(field.nome);
    setEditingFieldTipo(field.tipo);
    setEditingFieldDescricao(field.descricao || "");
    setEditingFieldModificaValor(field.modifica_valor_produto || false);
    setEditingFieldObrigatorio(field.obrigatorio || false);
    
    // Buscar dependência existente
    const dependency = formData?.dependencies?.find(
      (dep: any) => dep.field_id === field.id
    );
    setEditingFieldFiltradoPor(dependency?.depends_on_field_id || null);
  };

  const handleSaveField = () => {
    if (!editingFieldId || !editingFieldNome.trim() || !editingFieldTipo) return;

    // Se está ativando modifica_valor_produto, desativar em todos os outros campos
    if (editingFieldModificaValor) {
      // Desativar em todos os outros campos
      allFields.forEach((f: any) => {
        if (f.id !== editingFieldId && f.modifica_valor_produto) {
          updateFieldMutation.mutate({
            fieldId: f.id,
            nome: f.nome,
            tipo: f.tipo,
            descricao: f.descricao || undefined,
            modifica_valor_produto: false,
            obrigatorio: f.obrigatorio || false,
          });
        }
      });
    }

    updateFieldMutation.mutate({
      fieldId: editingFieldId,
      nome: editingFieldNome.trim(),
      tipo: editingFieldTipo,
      descricao: editingFieldDescricao.trim() || undefined,
      modifica_valor_produto: editingFieldModificaValor,
      obrigatorio: editingFieldObrigatorio,
    });
  };

  // Handler para salvar e abrir a aba do campo
  const handleSaveAndOpenFieldTab = () => {
    if (!editingFieldId || !editingFieldNome.trim() || !editingFieldTipo) return;

    // Se está ativando modifica_valor_produto, desativar em todos os outros campos
    if (editingFieldModificaValor) {
      // Desativar em todos os outros campos
      allFields.forEach((f: any) => {
        if (f.id !== editingFieldId && f.modifica_valor_produto) {
          updateFieldMutation.mutate({
            fieldId: f.id,
            nome: f.nome,
            tipo: f.tipo,
            descricao: f.descricao || undefined,
            modifica_valor_produto: false,
            obrigatorio: f.obrigatorio || false,
          });
        }
      });
    }

    // Salvar o campo
    updateFieldMutation.mutate(
      {
        fieldId: editingFieldId,
        nome: editingFieldNome.trim(),
        tipo: editingFieldTipo,
        descricao: editingFieldDescricao.trim() || undefined,
        modifica_valor_produto: editingFieldModificaValor,
        obrigatorio: editingFieldObrigatorio,
      },
      {
        onSuccess: () => {
          // Após salvar, mudar para a aba do campo
          setActiveTab(editingFieldId);
        },
      }
    );
  };

  // Handler para editar campo a partir da aba
  const handleEditFieldFromTab = (field: any) => {
    // Mudar para a aba "Todos os Campos"
    setActiveTab("all-fields");
    // Colocar o campo em modo de edição
    handleEditField(field);
  };

  const handleAddField = () => {
    if (!editingFieldNome.trim() || !editingFieldTipo) return;

    // Se está ativando modifica_valor_produto, desativar em todos os outros campos
    if (editingFieldModificaValor) {
      // Desativar em todos os outros campos
      allFields.forEach((f: any) => {
        if (f.modifica_valor_produto) {
          updateFieldMutation.mutate({
            fieldId: f.id,
            nome: f.nome,
            tipo: f.tipo,
            descricao: f.descricao || undefined,
            modifica_valor_produto: false,
            obrigatorio: f.obrigatorio || false,
          });
        }
      });
    }

    const maxOrdem = allFields.reduce((max: number, f: any) => Math.max(max, f.ordem || 0), 0);

    if (!currentFormId) {
      toast.error("Formulário não encontrado. Crie o formulário primeiro.");
      return;
    }

    createFieldMutation.mutate({
      nome: editingFieldNome.trim(),
      tipo: editingFieldTipo,
      descricao: editingFieldDescricao.trim() || undefined,
      modifica_valor_produto: editingFieldModificaValor,
      obrigatorio: editingFieldObrigatorio,
      ordem: maxOrdem + 1,
      currentFormId,
    });
  };

  const handleDragEndFields = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = allFields.findIndex((f: any) => f.id === active.id);
      const newIndex = allFields.findIndex((f: any) => f.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedFields = arrayMove(allFields, oldIndex, newIndex);
        const fieldsWithOrder = reorderedFields.map((field: any, index: number) => ({
          id: field.id,
          ordem: index,
        }));

        updateFieldOrderMutation.mutate(fieldsWithOrder);
      }
    }
  };

  // Componente para item sortable de campo
  const SortableFieldItem = ({ field }: { field: any }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: field.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const isEditing = editingFieldId === field.id;

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center gap-2 p-2 border rounded hover:bg-muted/50 ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
      >
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>

        {isEditing ? (
          <div className="flex-1 flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Nome do Campo</Label>
                <Input
                  value={editingFieldNome}
                  onChange={(e) => setEditingFieldNome(e.target.value)}
                  placeholder="Nome do campo"
                  className="h-8"
                  autoFocus
                />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Tipo do Campo</Label>
                <Select value={editingFieldTipo} onValueChange={setEditingFieldTipo}>
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Texto</SelectItem>
                    <SelectItem value="textarea">Área de Texto</SelectItem>
                    <SelectItem value="select">Seleção Única</SelectItem>
                    <SelectItem value="multiselect">Multiplas Seleções</SelectItem>
                    <SelectItem value="number">Número</SelectItem>
                    <SelectItem value="date">Data</SelectItem>
                    <SelectItem value="email">E-mail</SelectItem>
                    <SelectItem value="file">Arquivos</SelectItem>
                    <SelectItem value="image">Imagens</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Descrição do Campo</Label>
              <Input
                value={editingFieldDescricao}
                onChange={(e) => setEditingFieldDescricao(e.target.value)}
                placeholder="Texto explicativo para o usuário"
                className="h-8"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              {/* Campo: Filtrado pelo Campo (campo pai) */}
              <div className="space-y-1">
                <Label className="text-xs">Filtrado pelo Campo</Label>
                <Select
                  value={editingFieldFiltradoPor || "none"}
                  onValueChange={(value) => {
                    if (value === "none") {
                      // Remover dependência
                      if (editingFieldId) {
                        removeDependencyMutation.mutate(editingFieldId);
                        setEditingFieldFiltradoPor(null);
                      }
                    } else {
                      // Criar/atualizar dependência
                      if (editingFieldId) {
                        createOrUpdateDependencyMutation.mutate({
                          fieldId: editingFieldId,
                          dependsOnFieldId: value,
                        });
                        setEditingFieldFiltradoPor(value);
                      }
                    }
                  }}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Nenhum (campo independente)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum (campo independente)</SelectItem>
                    {availableParentFields.map((field: any) => (
                      <SelectItem key={field.id} value={field.id}>
                        {field.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Campo: Filtra o Campo (campos filhos) */}
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Filtra o Campo</Label>
                <div className="flex flex-wrap gap-2 min-h-[32px] items-center p-2 border rounded bg-muted/30">
                  {editingFieldId ? (
                    getChildFields(editingFieldId).length > 0 ? (
                      getChildFields(editingFieldId).map((field: any) => (
                        <Badge key={field.id} variant="secondary" className="text-xs">
                          {field.nome}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        Nenhum campo filho
                      </span>
                    )
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      Nenhum campo filho
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={editingFieldModificaValor}
                          onCheckedChange={setEditingFieldModificaValor}
                          disabled={isModificaValorProdutoDisabled}
                        />
                        <Label
                          className={`text-xs ${
                            isModificaValorProdutoDisabled
                              ? "text-muted-foreground cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                          onClick={() => {
                            if (!isModificaValorProdutoDisabled) {
                              setEditingFieldModificaValor(!editingFieldModificaValor);
                            }
                          }}
                        >
                          Modifica valor do produto
                        </Label>
                      </div>
                    </TooltipTrigger>
                    {isModificaValorProdutoDisabled && (
                      <TooltipContent>
                        <p>
                          Já existe outro campo com "Modifica valor do produto" ativo.
                          Apenas um campo pode ter esta opção ativa por vez.
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingFieldObrigatorio}
                    onCheckedChange={setEditingFieldObrigatorio}
                  />
                  <Label className="text-xs">Obrigatório</Label>
                </div>
              </div>
              <div className="flex gap-2">
                {(editingFieldTipo === "select" || editingFieldTipo === "multiselect") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveAndOpenFieldTab}
                    disabled={!editingFieldNome.trim() || !editingFieldTipo}
                  >
                    Opções
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleSaveField}
                  disabled={!editingFieldNome.trim() || !editingFieldTipo}
                >
                  <Save className="h-4 w-4 mr-1" />
                  Salvar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingFieldId(null);
                    setEditingFieldNome("");
                    setEditingFieldTipo("");
                    setEditingFieldDescricao("");
                    setEditingFieldModificaValor(false);
                    setEditingFieldObrigatorio(false);
                    setEditingFieldFiltradoPor(null);
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 flex items-center gap-2">
              <span className="text-sm font-medium">{field.nome}</span>
              {field.obrigatorio && (
                <span className="text-red-500 text-sm font-bold">*</span>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleEditField(field)}
            >
              <Edit className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    );
  };

  // Returns condicionais - TODOS os hooks devem estar antes desta linha
  if (!open) return null;

  // Se está criando um novo formulário e ainda não foi criado
  // Verifica se não tem formId E não tem currentFormId E não foi criado ainda
  if (!formId && !currentFormId && !formCreated) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Criar Novo Formulário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="form-nome">Nome do Formulário</Label>
              <Input
                id="form-nome"
                value={newFormNome}
                onChange={(e) => setNewFormNome(e.target.value)}
                placeholder="Digite o nome do formulário"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFormNome.trim()) {
                    createFormMutation.mutate(newFormNome.trim());
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  setNewFormNome("");
                  setFormCreated(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (newFormNome.trim()) {
                    createFormMutation.mutate(newFormNome.trim());
                  }
                }}
                disabled={!newFormNome.trim() || createFormMutation.isPending}
              >
                {createFormMutation.isPending ? "Criando..." : "Criar Formulário"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!currentFormId) return null;

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Sempre mostrar a interface de edição, mesmo quando não há campos
  // Isso permite que o usuário adicione campos através da aba "Todos os Campos"
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-lg">Edição Rápida do Formulário</DialogTitle>
        </DialogHeader>
        <div className="flex-1 flex overflow-hidden pb-4">
          <Tabs
            value={activeTab || "all-fields"}
            onValueChange={setActiveTab}
            className="flex-1 flex"
          >
            <div className="w-48 border-r bg-muted/50 flex flex-col">
              <TabsList className="flex-col h-auto w-full rounded-none bg-transparent p-2 gap-1">
                <TabsTrigger
                  value="all-fields"
                  className="w-full justify-start text-left data-[state=active]:bg-blue-900 data-[state=active]:text-white hover:bg-gray-500 hover:text-white"
                >
                  Todos os Campos
                </TabsTrigger>
                {selectFields.map((field: any) => (
                  <TabsTrigger
                    key={field.id}
                    value={field.id}
                    className="w-full justify-between text-left data-[state=active]:bg-blue-900 data-[state=active]:text-white hover:bg-gray-500 hover:text-white"
                  >
                    <span>{field.nome}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 hover:bg-muted data-[state=active]:text-white hover:text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditFieldFromTab(field);
                      }}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <div className="flex-1 overflow-y-auto">
              {selectFields.map((field: any) => {
                const allOptions = flattenOptions(field.options || []);
                return (
                  <TabsContent
                    key={field.id}
                    value={field.id}
                    className="m-0 p-4 pt-0"
                  >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-sm">{field.nome}</h3>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddOption(field.id)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar
                      </Button>
                    </div>
                    <div className="space-y-1">
                      {allOptions.map((option) => (
                        <div
                          key={option.id}
                          className="flex items-center gap-2 p-2 border rounded hover:bg-muted/50"
                        >
                          {editingOptionId === option.id ? (
                            <div className="flex-1 flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <Input
                                  value={editingLabel}
                                  onChange={(e) => setEditingLabel(e.target.value)}
                                  className="h-8 flex-1"
                                  autoFocus
                                />
                                {field.modifica_valor_produto && (
                                  <div className="flex items-center gap-2 w-32">
                                    <Label className="text-xs whitespace-nowrap">Valor:</Label>
                                    <MoneyInput
                                      value={editingProductPrice ?? 0}
                                      onValueChange={(value) => setEditingProductPrice(value)}
                                      className="h-8"
                                      placeholder="0,00"
                                    />
                                  </div>
                                )}
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleSaveEdit(field.modifica_valor_produto)}
                                >
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={handleCancelEdit}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              {/* Edição de relacionamentos */}
                              {(() => {
                                // Se é campo filho, mostrar relacionamentos com campo pai
                                const parentField = getParentField(field.id);
                                if (parentField) {
                                  const relatedOptions = getRelatedOptions(option.id);
                                  const parentOptions = flattenOptions(parentField.options || []);
                                  const availableParentOptions = parentOptions.filter(
                                    (opt: any) => !relatedOptions.some((ro: any) => ro.id === opt.id)
                                  );
                                  
                                  return (
                                    <div className="flex items-center gap-2 flex-wrap pl-2 border-l-2 border-muted">
                                      <span className="text-xs text-muted-foreground">Relacionado com:</span>
                                      {relatedOptions.map((relOpt: any) => (
                                        <Badge
                                          key={relOpt.id}
                                          className="flex items-center gap-1 bg-blue-900 text-white hover:bg-blue-800 text-sm py-[1px] px-2.5"
                                        >
                                          {relOpt.label}
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setDeletingRelation({
                                                  optionId: option.id,
                                                  relatedOptionId: relOpt.id,
                                                })
                                              }
                                              className="ml-1 hover:text-red-300"
                                            >
                                              <X className="h-4 w-4" />
                                            </button>
                                        </Badge>
                                      ))}
                                      {availableParentOptions.length > 0 && (
                                        <div className="flex items-center gap-1 flex-wrap">
                                          {availableParentOptions.map((opt: any) => (
                                            <Button
                                              key={opt.id}
                                              type="button"
                                              size="sm"
                                              variant="outline"
                                              className="h-6 text-xs"
                                              onClick={() => {
                                                createRelationMutation.mutate({
                                                  optionId: option.id,
                                                  relatedOptionId: opt.id,
                                                });
                                              }}
                                            >
                                              + {opt.label}
                                            </Button>
                                          ))}
                                        </div>
                                      )}
                                      <div className="flex items-center rounded-md border border-blue-900 bg-white overflow-hidden">
                                        <Input
                                          value={newOptionInputs[`${parentField.id}-${option.id}`] || ""}
                                          onChange={(e) => {
                                            setNewOptionInputs((prev) => ({
                                              ...prev,
                                              [`${parentField.id}-${option.id}`]: e.target.value,
                                            }));
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              const inputValue = newOptionInputs[`${parentField.id}-${option.id}`];
                                              if (inputValue?.trim()) {
                                                handleCreateAndRelateOption(
                                                  parentField.id,
                                                  inputValue,
                                                  option.id,
                                                  false
                                                );
                                              }
                                            }
                                          }}
                                          placeholder="Nova opção..."
                                          className="h-auto py-[1px] px-2.5 text-sm text-foreground bg-white border-0 rounded-none rounded-l-md focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-blue-900 w-auto min-w-[120px]"
                                        />
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="ghost"
                                          className="h-auto py-[1px] px-2.5 text-sm text-white bg-blue-900 hover:bg-blue-800 rounded-none rounded-r-md border-0 border-l border-blue-800 w-auto"
                                          onClick={() => {
                                            const inputValue = newOptionInputs[`${parentField.id}-${option.id}`];
                                            if (inputValue?.trim()) {
                                              handleCreateAndRelateOption(
                                                parentField.id,
                                                inputValue,
                                                option.id,
                                                false
                                              );
                                            }
                                          }}
                                        >
                                          Inserir
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                }

                                // Se é campo pai, mostrar opções dos campos filhos relacionadas
                                const childFields = getChildFields(field.id);
                                if (childFields.length > 0) {
                                  const childOptions = getChildOptionsRelatedToParent(option.id);
                                  
                                  return (
                                    <div className="flex flex-col gap-2 pl-2 border-l-2 border-muted">
                                      {childFields.map((childField: any) => {
                                        const childFieldOptions = flattenOptions(childField.options || []);
                                        const relatedChildOptions = childOptions.filter((opt: any) => {
                                          return childFieldOptions.some((co: any) => co.id === opt.id);
                                        });
                                        const availableChildOptions = childFieldOptions.filter(
                                          (opt: any) => !relatedChildOptions.some((ro: any) => ro.id === opt.id)
                                        );
                                        
                                        return (
                                          <div key={childField.id} className="flex flex-col gap-1">
                                            <span className="text-xs font-medium text-muted-foreground">
                                              {childField.nome}:
                                            </span>
                                            <div className="flex items-center gap-2 flex-wrap">
                                              {relatedChildOptions.map((relOpt: any) => (
                                                <Badge
                                                  key={relOpt.id}
                                                  className="flex items-center gap-1 bg-blue-900 text-white hover:bg-blue-800 text-sm py-[1px] px-2.5"
                                                >
                                                  {relOpt.label}
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        setDeletingRelation({
                                                          optionId: relOpt.id,
                                                          relatedOptionId: option.id,
                                                        })
                                                      }
                                                      className="ml-1 hover:text-red-300"
                                                    >
                                                      <X className="h-4 w-4" />
                                                    </button>
                                                </Badge>
                                              ))}
                                              {availableChildOptions.length > 0 && (
                                                <div className="flex items-center gap-1 flex-wrap">
                                                  {availableChildOptions.map((opt: any) => (
                                                    <Button
                                                      key={opt.id}
                                                      type="button"
                                                      size="sm"
                                                      variant="outline"
                                                      className="h-6 text-xs"
                                                      onClick={() => {
                                                        createRelationMutation.mutate({
                                                          optionId: opt.id,
                                                          relatedOptionId: option.id,
                                                        });
                                                      }}
                                                    >
                                                      + {opt.label}
                                                    </Button>
                                                  ))}
                                                </div>
                                              )}
                                              <div className="flex items-center rounded-md border border-blue-900 bg-white overflow-hidden">
                                                <Input
                                                  value={newOptionInputs[`${childField.id}-${option.id}`] || ""}
                                                  onChange={(e) => {
                                                    setNewOptionInputs((prev) => ({
                                                      ...prev,
                                                      [`${childField.id}-${option.id}`]: e.target.value,
                                                    }));
                                                  }}
                                                  onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                      e.preventDefault();
                                                      const inputValue = newOptionInputs[`${childField.id}-${option.id}`];
                                                      if (inputValue?.trim()) {
                                                        handleCreateAndRelateOption(
                                                          childField.id,
                                                          inputValue,
                                                          option.id,
                                                          true
                                                        );
                                                      }
                                                    }
                                                  }}
                                                  placeholder="Nova opção..."
                                                  className="h-auto py-[1px] px-2.5 text-sm text-foreground bg-white border-0 rounded-none rounded-l-md focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-blue-900 w-auto min-w-[120px]"
                                                />
                                                <Button
                                                  type="button"
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-auto py-[1px] px-2.5 text-sm text-white bg-blue-900 hover:bg-blue-800 rounded-none rounded-r-md border-0 border-l border-blue-800 w-auto"
                                                  onClick={() => {
                                                    const inputValue = newOptionInputs[`${childField.id}-${option.id}`];
                                                    if (inputValue?.trim()) {
                                                      handleCreateAndRelateOption(
                                                        childField.id,
                                                        inputValue,
                                                        option.id,
                                                        true
                                                      );
                                                    }
                                                  }}
                                                >
                                                  Inserir
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                }

                                return null;
                              })()}
                            </div>
                          ) : (
                            <>
                              <div className="flex-1 flex items-center gap-4 text-sm">
                                <span className="font-medium">{option.label}</span>
                                {field.modifica_valor_produto &&
                                  option.valor_produto !== null &&
                                  option.valor_produto !== undefined && (
                                    <span className="text-xs text-primary font-medium">
                                      {new Intl.NumberFormat("pt-BR", {
                                        style: "currency",
                                        currency: "BRL",
                                      }).format(option.valor_produto)}
                                    </span>
                                  )}
                                {(() => {
                                  // Se é campo filho, mostrar relacionamentos com campo pai
                                  const parentField = getParentField(field.id);
                                  if (parentField) {
                                    const relatedOptions = getRelatedOptions(option.id);
                                    if (relatedOptions.length === 0) return null;
                                    return (
                                      <span className="text-xs text-muted-foreground">
                                        {relatedOptions.map((relOpt: any) => relOpt.label).join(", ")}
                                      </span>
                                    );
                                  }

                                  // Se é campo pai, mostrar opções dos campos filhos relacionadas
                                  const childFields = getChildFields(field.id);
                                  if (childFields.length > 0) {
                                    const childOptions = getChildOptionsRelatedToParent(option.id);
                                    if (childOptions.length === 0) return null;
                                    return (
                                      <span className="text-xs text-muted-foreground">
                                        {childOptions.map((relOpt: any) => relOpt.label).join(", ")}
                                      </span>
                                    );
                                  }

                                  return null;
                                })()}
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditOption(option)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeletingOptionId(option.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      ))}
                      {allOptions.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Nenhuma opção cadastrada
                        </p>
                      )}
                    </div>
                  </div>
                  </TabsContent>
                );
              })}
              
              {/* Aba Todos os Campos */}
              <TabsContent
                value="all-fields"
                className="m-0 p-4 pt-0"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium">Todos os Campos</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsAddingNewField(true);
                        setEditingFieldNome("");
                        setEditingFieldTipo("");
                        setEditingFieldDescricao("");
                        setEditingFieldModificaValor(false);
                        setEditingFieldObrigatorio(false);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar Campo
                    </Button>
                  </div>

                  {isAddingNewField && (
                    <div className="p-3 border rounded bg-muted/50">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-4">
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs">Nome do Campo</Label>
                            <Input
                              value={editingFieldNome}
                              onChange={(e) => setEditingFieldNome(e.target.value)}
                              placeholder="Nome do campo"
                              className="h-8"
                              autoFocus
                            />
                          </div>
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs">Tipo do Campo</Label>
                            <Select value={editingFieldTipo} onValueChange={setEditingFieldTipo}>
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Tipo" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">Texto</SelectItem>
                                <SelectItem value="textarea">Área de Texto</SelectItem>
                                <SelectItem value="select">Seleção Única</SelectItem>
                                <SelectItem value="multiselect">Multiplas Seleções</SelectItem>
                                <SelectItem value="number">Número</SelectItem>
                                <SelectItem value="date">Data</SelectItem>
                                <SelectItem value="email">E-mail</SelectItem>
                                <SelectItem value="file">Arquivos</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Descrição do Campo</Label>
                          <Input
                            value={editingFieldDescricao}
                            onChange={(e) => setEditingFieldDescricao(e.target.value)}
                            placeholder="Texto explicativo para o usuário"
                            className="h-8"
                          />
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={editingFieldModificaValor}
                                      onCheckedChange={setEditingFieldModificaValor}
                                      disabled={isModificaValorProdutoDisabled}
                                    />
                                    <Label
                                      className={`text-xs ${
                                        isModificaValorProdutoDisabled
                                          ? "text-muted-foreground cursor-not-allowed"
                                          : "cursor-pointer"
                                      }`}
                                      onClick={() => {
                                        if (!isModificaValorProdutoDisabled) {
                                          setEditingFieldModificaValor(!editingFieldModificaValor);
                                        }
                                      }}
                                    >
                                      Modifica valor do produto
                                    </Label>
                                  </div>
                                </TooltipTrigger>
                                {isModificaValorProdutoDisabled && (
                                  <TooltipContent>
                                    <p>
                                      Já existe outro campo com "Modifica valor do produto" ativo.
                                      Apenas um campo pode ter esta opção ativa por vez.
                                    </p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={editingFieldObrigatorio}
                                onCheckedChange={setEditingFieldObrigatorio}
                              />
                              <Label className="text-xs">Obrigatório</Label>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleAddField}
                              disabled={!editingFieldNome.trim() || !editingFieldTipo}
                            >
                              <Save className="h-4 w-4 mr-1" />
                              Salvar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setIsAddingNewField(false);
                                setEditingFieldNome("");
                                setEditingFieldTipo("");
                                setEditingFieldDescricao("");
                                setEditingFieldModificaValor(false);
                                setEditingFieldObrigatorio(false);
                              }}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEndFields}
                  >
                    <SortableContext
                      items={allFields.map((f: any) => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-1">
                        {allFields.map((field: any) => (
                          <SortableFieldItem key={field.id} field={field} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>

                  {allFields.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhum campo cadastrado
                    </p>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </DialogContent>
      
      {/* Dialog de confirmação para exclusão de opção */}
      <AlertDialog open={!!deletingOptionId} onOpenChange={(open) => !open && setDeletingOptionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta opção? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingOptionId) {
                  deleteOptionMutation.mutate(deletingOptionId);
                  setDeletingOptionId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmação para exclusão de relacionamento */}
      <AlertDialog open={!!deletingRelation} onOpenChange={(open) => !open && setDeletingRelation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar remoção</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este relacionamento?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingRelation) {
                  deleteRelationMutation.mutate({
                    optionId: deletingRelation.optionId,
                    relatedOptionId: deletingRelation.relatedOptionId,
                  });
                  setDeletingRelation(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

